<template>
    <div>
        <v-card>
            <v-card-title ref="title">
                <v-btn
                    class="mr-3"
                    depressed
                    color="primary"
                    icon
                    rounded
                    small
                    @click="closeTimeLine"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <h3>
                    {{
                        language === 'spanish'
                            ? 'Timeline cotización'
                            : 'Quote timeline'
                    }}
                </h3>
            </v-card-title>
            <v-divider class="ml-4 mr-5"></v-divider>
            <v-card-text
                class="mt-4 overflow-y-auto"
                :style="{ 'max-height': `${height}px` }"
            >
                <v-timeline v-if="events.length" dense class="pr-6" align-top>
                    <v-timeline-item
                        v-for="(event, index) in events"
                        :key="event.id || `${event.type}-${index}`"
                        :color="dotColorForEvent(event)"
                        small
                    >
                        <v-row justify="space-between" no-gutters>
                            <v-col cols="6">
                                <div class="font-weight-bold">
                                    {{ getTitle(event) }}
                                </div>
                                <div class="text-body-2">
                                    {{ getUserName(event.userId) }}
                                </div>
                                <div
                                    class="text-caption"
                                    v-if="getStatusText(event)"
                                >
                                    {{ getStatusText(event) }}
                                </div>
                                <div
                                    class="text-caption"
                                    v-if="
                                        event.meta && event.meta.approvalMethod
                                    "
                                >
                                    {{ event.meta.approvalMethod }}
                                </div>
                            </v-col>
                            <v-col cols="6" class="text-right text-no-wrap">
                                <span class="text-caption">{{
                                    formatDate(event.date)
                                }}</span>
                            </v-col>
                        </v-row>
                    </v-timeline-item>
                </v-timeline>
                <v-alert v-else dense text type="info">
                    {{
                        language === 'spanish'
                            ? 'Sin eventos todavía.'
                            : 'No events yet.'
                    }}
                </v-alert>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import moment from 'moment'

export default {
    name: 'QuoteTimeline',
    props: {
        quote: {
            type: Object,
            required: true,
        },
        users: {
            type: Array,
            default: () => [],
        },
        contacts: {
            type: Array,
            default: () => [],
        },
        language: {
            type: String,
            default: 'english',
        },
    },
    data() {
        return {
            height: 0,
            quoteUsers: [],
        }
    },
    computed: {
        events() {
            const quote = this.quote || {}
            const timeline = Array.isArray(quote.clientTimeline)
                ? quote.clientTimeline
                : Array.isArray(quote.clientTimeLine)
                ? quote.clientTimeLine
                : this.buildLegacyTimeline(quote)

            const withRequested = this.ensureRequested(timeline, quote)

            return [...withRequested].sort(
                (a, b) =>
                    this.getMillis(a && a.date) - this.getMillis(b && b.date)
            )
        },
    },
    mounted() {
        this.quoteUsers = this.users
        this.quoteUsers.push(...this.contacts)
        this.$nextTick(() => {
            this.onResize()
        })
    },
    methods: {
        onResize() {
            const title = this.$refs.title
            const titleHeight = title ? title.clientHeight : 0
            this.height = window.innerHeight - titleHeight - 90
        },
        closeTimeLine() {
            this.$emit('closeDialog')
        },
        buildLegacyTimeline(quote) {
            const timeline = []
            const versionNumber = Number.isFinite(
                Math.floor(Number(quote.number))
            )
                ? Math.floor(Number(quote.number))
                : null

            if (quote.submittedOn) {
                timeline.push({
                    id: 'legacy-quote-submitted',
                    type: 'QUOTE_APPROVAL',
                    userId: quote.createdBy || null,
                    date: quote.submittedOn,
                    source: 'portal',
                    versionNumber,
                })
            }

            if (quote.sentDate) {
                timeline.push({
                    id: 'legacy-quote-sent',
                    type: 'QUOTE_SENT',
                    userId: quote.createdBy || null,
                    date: quote.sentDate,
                    source: 'portal',
                    versionNumber,
                })
            }

            if (quote.preApprovedOn || quote.preApprovedBy) {
                timeline.push({
                    id: 'legacy-client-pre-approved',
                    type: 'CLIENT_PRE_APPROVED',
                    userId: quote.preApprovedBy || null,
                    date: quote.preApprovedOn || null,
                    source: 'portal',
                    versionNumber,
                })
            }

            if (quote.awardInProgress && quote.awardInProgress.approvedOn) {
                timeline.push({
                    id: 'legacy-quote-approved',
                    type: 'QUOTE_APPROVED',
                    userId: quote.awardInProgress.approvedBy || null,
                    date: quote.awardInProgress.approvedOn,
                    source: 'portal',
                    versionNumber,
                })
            }

            return timeline
        },
        ensureRequested(timeline, quote) {
            const list = Array.isArray(timeline) ? [...timeline] : []
            const hasRequested = list.some(
                item => item && item.type === 'QUOTE_REQUESTED'
            )

            if (!hasRequested && quote && quote.createdOn) {
                const versionNumber = Number.isFinite(
                    Math.floor(Number(quote.number))
                )
                    ? Math.floor(Number(quote.number))
                    : null

                list.unshift({
                    id: 'legacy-quote-requested',
                    type: 'QUOTE_REQUESTED',
                    userId: quote.createdBy || null,
                    date: quote.createdOn || null,
                    source: 'portal',
                    versionNumber,
                })
            }

            return list
        },
        getMillis(value) {
            if (!value) return 0
            if (typeof value.toDate === 'function')
                return value.toDate().getTime()
            if (value.seconds !== undefined && value.seconds !== null)
                return Number(value.seconds) * 1000
            if (value._seconds !== undefined && value._seconds !== null)
                return Number(value._seconds) * 1000
            if (value instanceof Date) return value.getTime()
            if (typeof value === 'number') return value
            const parsed = new Date(value).getTime()
            return Number.isNaN(parsed) ? 0 : parsed
        },
        formatDate(value) {
            const millis = this.getMillis(value)
            if (!millis) return '-'
            return moment(millis).format('MMM/DD/YYYY h:mm a')
        },
        getTitle(event) {
            const labels = {
                QUOTE_REQUESTED:
                    this.language === 'spanish'
                        ? 'Cotización solicitada'
                        : 'Quote requested',
                QUOTE_SENT:
                    this.language === 'spanish'
                        ? 'Cotización enviada'
                        : 'Quote sent',
                QUOTE_APPROVAL:
                    this.language === 'spanish'
                        ? 'Cotización en progreso'
                        : 'Quote in progress',
                QUOTE_SUBMITTED:
                    this.language === 'spanish'
                        ? 'Cotización en progreso'
                        : 'Quote in progress',
                MODIFICATION_REQUESTED:
                    this.language === 'spanish'
                        ? 'Modificación solicitada'
                        : 'Modification requested',
                CLIENT_PRE_APPROVED:
                    this.language === 'spanish'
                        ? 'Preaprobación cliente'
                        : 'Client pre-approved',
                CLIENT_APPROVED:
                    this.language === 'spanish'
                        ? 'Aprobación cliente'
                        : 'Client approved',
            }

            return labels[event.type] || event.type
        },
        mapUiStatus(status) {
            if (!status) return '-'
            if (status === 'OPEN') return 'Bidding'
            if (status === 'APPROVAL' || status === 'SENT') return 'Active'
            if (status === 'clientPreApproved') return 'Pre-Approved'
            if (status === 'clientApproved') return 'Approved'
            return status
        },
        getStatusText(event) {
            if (!event.fromStatus && !event.toStatus) return ''
            return `${this.mapUiStatus(event.fromStatus)} -> ${this.mapUiStatus(
                event.toStatus
            )}`
        },
        getUserName(userId) {
            if (userId === 'System') return 'System'
            if (!userId)
                return this.language === 'spanish' ? 'Desconocido' : 'Unknown'
            const user = this.quoteUsers.find(
                u => String(u.id) === String(userId)
            )
            return (
                (user && user.name) ||
                (this.language === 'spanish' ? 'Desconocido' : 'Unknown')
            )
        },
        dotColorForEvent(event) {
            const type = event && event.type
            if (type === 'QUOTE_REQUESTED') return 'grey'
            if (type === 'QUOTE_SENT') return 'primary'
            if (type === 'MODIFICATION_REQUESTED') return '#FF9800'
            if (type === 'CLIENT_PRE_APPROVED') return 'warning'
            if (type === 'CLIENT_APPROVED' || type === 'QUOTE_APPROVED')
                return '#27BC4F'
            if (type === 'QUOTE_APPROVAL' || type === 'QUOTE_SUBMITTED')
                return '#3293e6'
            return 'grey'
        },
    },
}
</script>
